import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useDemoMode } from "@/contexts/DemoModeContext";
import { useScreenshotMode } from "@/contexts/ScreenshotModeContext";
import { SCENARIOS, SCENARIO_ORDER } from "@/lib/demoMode/scenarios";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Sparkles, EyeOff } from "lucide-react";

interface DemoModeToggleProps {
  variant?: "floating" | "inline";
}

/**
 * Floating admin-only Demo Mode control. Sits in the bottom-right of every
 * authenticated screen. Lets admins overlay one of three readiness scenarios
 * on top of their real data (read-only — nothing is written to the DB).
 */
const DemoModeToggle = ({ variant = "floating" }: DemoModeToggleProps = {}) => {
  const { isAdmin, isSuperAdmin } = useAuth();
  const { scenario, setScenario, isDemo } = useDemoMode();
  const { enabled: screenshotMode } = useScreenshotMode();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  if (!isAdmin && !isSuperAdmin) return null;
  if (screenshotMode) return null;
  // Only surface the floating control inside the admin area so it never
  // appears in learner-dashboard screenshots or marketing videos.
  if (!location.pathname.startsWith("/admin")) return null;

  const wrapperClass =
    variant === "inline"
      ? "w-full print:hidden"
      : "fixed bottom-4 right-4 z-[60] print:hidden";
  const triggerClass =
    variant === "inline"
      ? (isDemo
          ? "w-full justify-start bg-primary text-primary-foreground hover:bg-primary/90 gap-2"
          : "w-full justify-start bg-sidebar-accent text-sidebar-foreground hover:bg-sidebar-accent/80 gap-2")
      : (isDemo
          ? "shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 gap-2"
          : "shadow-lg bg-foreground/85 text-background hover:bg-foreground gap-2");

  return (
    <div className={wrapperClass}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            size="sm"
            className={triggerClass}
            aria-label="Demo Mode"
          >
            <Sparkles className="h-4 w-4" />
            {isDemo && scenario ? `Demo: ${SCENARIOS[scenario].label}` : "Demo Mode"}
          </Button>
        </PopoverTrigger>
        <PopoverContent align={variant === "inline" ? "start" : "end"} side={variant === "inline" ? "right" : "top"} className="w-72 p-3">
          <div className="space-y-2">
            <div className="px-1">
              <div className="text-sm font-semibold text-foreground">Demo Mode</div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Overlay realistic data over your account for screenshots and
                presentations. Nothing is saved.
              </p>
            </div>
            <div className="space-y-1.5 pt-1">
              {SCENARIO_ORDER.map((id) => {
                const cfg = SCENARIOS[id];
                const active = scenario === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => { setScenario(id); setOpen(false); }}
                    className={
                      "w-full text-left rounded-md border px-3 py-2 transition-colors " +
                      (active
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted")
                    }
                  >
                    <div className="text-sm font-medium text-foreground flex items-center justify-between">
                      <span>{cfg.label}</span>
                      {active && <span className="text-[10px] uppercase tracking-wide text-primary font-semibold">Active</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{cfg.description}</div>
                  </button>
                );
              })}
            </div>
            <div className="pt-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-2 text-muted-foreground"
                disabled={!isDemo}
                onClick={() => { setScenario(null); setOpen(false); }}
              >
                <EyeOff className="h-4 w-4" />
                Turn off — restore real data
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DemoModeToggle;